import {
    ChangeDetectionStrategy,
    Component,
    InjectionToken,
    computed,
    inject,
    signal,
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {DateTime} from 'luxon';
import {BehaviorSubject, catchError, EMPTY, finalize, map, of, startWith, switchMap} from 'rxjs';
import {
    TRANSLATE,
    TranslatePipe,
    caseInsensitiveComparator,
    composeComparators,
    includesIgnoreCase,
    keyComparator,
} from '@brunata-angular/util';
import {DateTimePipe, FormatBytesPipe, InputDateGroupComponent, Sorting} from '@brunata-angular/ui';
import {
    ExportTableColumn,
    ExportTableComponent,
} from '../../shared/ui/export-table/export-table.component';
import {ActionButtonComponent} from '../../shared/ui/action-button/action-button.component';
import {SftpConnectionsService} from '../sftp-connections.service';
import {SftpConnection, SftpTestServerFile, SftpTestServerFiles} from '../sftp-connection.model';

export type SftpTestServerFilesModalInput = {
    readonly sftpConnection: SftpConnection;
};

type FilesState =
    | {status: 'loading'}
    | {status: 'unavailable'}
    | {status: 'loaded'; result: SftpTestServerFiles};

const defaultSorting: Sorting = {column: 'modifiedAt', direction: 'desc'};

@Component({
    selector: 'app-sftp-test-server-files-modal',
    imports: [
        ActionButtonComponent,
        DateTimePipe,
        FormatBytesPipe,
        InputDateGroupComponent,
        ReactiveFormsModule,
        TranslatePipe,
        ExportTableComponent,
    ],
    templateUrl: './sftp-test-server-files-modal.component.html',
    styles: `
        .modal-body {
            height: 70vh;
        }

        .sftp-test-server-files-search {
            min-width: 12.5rem;
        }

        .sftp-test-server-files-date {
            min-width: 11rem;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SftpTestServerFilesModalComponent {
    static readonly inputToken = new InjectionToken<SftpTestServerFilesModalInput>(
        'SftpTestServerFilesModalComponent.input',
    );

    protected readonly activeModal = inject(NgbActiveModal);
    protected readonly sftpConnection = inject(SftpTestServerFilesModalComponent.inputToken)
        .sftpConnection;

    private readonly sftpConnectionsService = inject(SftpConnectionsService);
    private readonly translate = inject(TRANSLATE);
    private readonly refresh$ = new BehaviorSubject<void>(undefined);

    protected readonly filters = new FormGroup({
        search: new FormControl('', {nonNullable: true}),
        modifiedOn: new FormControl<DateTime | null>(null),
    });

    protected readonly sorting = signal(defaultSorting);

    protected readonly columns: ExportTableColumn[] = [
        {
            label: this.translate(
                'sftpconnection_testserverfiles_column_folder',
                'online',
                'export',
            ),
            sortColumn: 'folder',
            width: '22%',
        },
        {
            label: this.translate(
                'sftpconnection_testserverfiles_column_file_name',
                'online',
                'export',
            ),
            sortColumn: 'fileName',
        },
        {
            label: this.translate('sftpconnection_testserverfiles_column_size', 'online', 'export'),
            sortColumn: 'sizeBytes',
            width: '9%',
        },
        {
            label: this.translate(
                'sftpconnection_testserverfiles_column_modified',
                'online',
                'export',
            ),
            sortColumn: 'modifiedAt',
            width: '20%',
        },
        {label: '', alignEnd: true, width: '12%'},
    ];

    protected readonly downloadingFile = signal<SftpTestServerFile | null>(null);

    protected readonly state = toSignal(
        this.refresh$.pipe(
            switchMap(() =>
                this.sftpConnectionsService
                    .getSftpTestServerFiles(this.sftpConnection.sftpConnectionId)
                    .pipe(
                        map((result): FilesState => ({status: 'loaded', result})),
                        catchError(() => of<FilesState>({status: 'unavailable'})),
                        startWith<FilesState>({status: 'loading'}),
                    ),
            ),
        ),
        {initialValue: {status: 'loading'}},
    );

    private readonly filterValues = toSignal(
        this.filters.valueChanges.pipe(map(() => this.filters.getRawValue())),
        {initialValue: this.filters.getRawValue()},
    );

    private readonly loadedFiles = computed(() => {
        const current = this.state();
        return current.status === 'loaded' ? current.result.files : [];
    });

    protected readonly fileCount = computed(() => this.loadedFiles().length);

    protected readonly failureMessage = computed(() => {
        const current = this.state();
        return current.status === 'loaded' ? current.result.failureMessage : null;
    });

    protected readonly files = computed(() => {
        const files = this.loadedFiles();
        const {search, modifiedOn} = this.filterValues();
        const matching = files.filter(
            file =>
                (includesIgnoreCase(file.folder, search) ||
                    includesIgnoreCase(file.fileName, search)) &&
                (modifiedOn === null ||
                    !modifiedOn.isValid ||
                    file.modifiedAt.toISODate() === modifiedOn.toISODate()),
        );
        return this.sorted(matching);
    });

    protected refresh(): void {
        this.refresh$.next();
    }

    protected onSort(sorting: Sorting): void {
        this.sorting.set(sorting);
    }

    protected resetFilters(): void {
        this.filters.setValue({search: '', modifiedOn: null});
        this.sorting.set(defaultSorting);
    }

    protected download(file: SftpTestServerFile): void {
        this.downloadingFile.set(file);
        this.sftpConnectionsService
            .downloadSftpTestServerFile(this.sftpConnection.sftpConnectionId, file)
            .pipe(
                catchError(() => EMPTY),
                finalize(() => this.downloadingFile.set(null)),
            )
            .subscribe();
    }

    private sorted(files: SftpTestServerFile[]): SftpTestServerFile[] {
        const {column, direction} = this.sorting();
        const byFileName = caseInsensitiveComparator<SftpTestServerFile>(
            file => file.fileName,
            direction,
        );
        const comparator =
            column === 'folder'
                ? composeComparators<SftpTestServerFile>([
                      caseInsensitiveComparator(file => file.folder, direction),
                      byFileName,
                  ])
                : column === 'fileName'
                  ? byFileName
                  : column === 'sizeBytes'
                    ? keyComparator<SftpTestServerFile, number>(file => file.sizeBytes, direction)
                    : keyComparator<SftpTestServerFile, number>(
                          file => file.modifiedAt.toMillis(),
                          direction,
                      );
        return [...files].sort(comparator);
    }
}
